import type { Permission } from '@re/shared';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, refreshSession, setAccessToken, setSessionLostHandler } from '../api/client';
import type { MeResponse } from '../api/types';

export const SLUG_KEY = 'org.lastSlug';

interface AuthState {
  status: 'loading' | 'anonymous' | 'authenticated';
  me: MeResponse | null;
  /** true when the user holds at least one of the given permissions */
  can: (...perms: Permission[]) => boolean;
  login: (slug: string, username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  reload: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<AuthState['status']>('loading');
  const [me, setMe] = useState<MeResponse | null>(null);

  const loadMe = useCallback(async () => {
    const m = await api<MeResponse>('/me');
    setMe(m);
    setStatus('authenticated');
  }, []);

  useEffect(() => {
    setSessionLostHandler(() => {
      setMe(null);
      setStatus('anonymous');
      qc.clear();
    });
    refreshSession().then((ok) => (ok ? loadMe().catch(() => setStatus('anonymous')) : setStatus('anonymous')));
  }, [loadMe, qc]);

  const login = useCallback(
    async (slug: string, username: string, password: string) => {
      const r = await api<{ accessToken: string }>('/auth/login', { method: 'POST', body: { slug, username, password } });
      setAccessToken(r.accessToken);
      try {
        localStorage.setItem(SLUG_KEY, slug);
      } catch {
        /* ignore */
      }
      await loadMe();
    },
    [loadMe],
  );

  const logout = useCallback(async () => {
    await api('/auth/logout', { method: 'POST', body: {} }).catch(() => undefined);
    setAccessToken(null);
    setMe(null);
    setStatus('anonymous');
    qc.clear();
  }, [qc]);

  // a manager may change this employee's permissions at any time: re-read them every minute and when the tab is shown again
  useEffect(() => {
    if (status !== 'authenticated') return;
    const refresh = () => void loadMe().catch(() => undefined);
    const timer = setInterval(refresh, 60_000);
    const onVisible = () => document.visibilityState === 'visible' && refresh();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [status, loadMe]);

  const can = useCallback((...perms: Permission[]) => !!me && perms.some((p) => me.permissions.includes(p)), [me]);

  const value = useMemo(() => ({ status, me, can, login, logout, reload: loadMe }), [status, me, can, login, logout, loadMe]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
